import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getDiscountedPrice } from '../utils/pricing';
import { trackAddToCart } from '../lib/analytics';

const CartContext = createContext();

const getPriceByType = (product, orderType, quantity = 1) => {
  switch (orderType) {
    case 'stems':
      return getDiscountedPrice(product.pricePerStem, quantity);
    case 'subscription-weekly':
      return product.subscriptionWeekly;
    case 'subscription-monthly':
      return product.subscriptionMonthly;
    default:
      return product.pricePerStem;
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('farmfreshlily_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('farmfreshlily_cart', JSON.stringify(cart));
  }, [cart]);

  // setCart is stable; functional-update pattern reads `prev` inside the callback,
  // so no closure over `cart` needed. Module-scope helpers (getPriceByType,
  // getDiscountedPrice) are pure and not deps. Empty deps below are intentional.
  const addToCart = useCallback((product, orderType, quantity) => {
    const price = getPriceByType(product, orderType, quantity);
    trackAddToCart(product, orderType, quantity, price);
    setCart(prev => {
      const existingItem = prev.find(
        item => item.productId === product.id && item.orderType === orderType
      );
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        const newPrice = getPriceByType(product, orderType, newQuantity);
        return prev.map(item =>
          item.productId === product.id && item.orderType === orderType
            ? { ...item, quantity: newQuantity, price: newPrice, pricePerStem: product.pricePerStem }
            : item
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          image: product.image,
          orderType,
          quantity,
          price,
          pricePerStem: product.pricePerStem,
        },
      ];
    });
  }, []);

  const removeFromCart = useCallback((productId, orderType) => {
    setCart(prev => prev.filter(item => !(item.productId === productId && item.orderType === orderType)));
  }, []);

  const updateQuantity = useCallback((productId, orderType, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => !(item.productId === productId && item.orderType === orderType)));
      return;
    }
    setCart(prev => prev.map(item => {
      if (item.productId === productId && item.orderType === orderType) {
        let newPrice = item.price;
        if (orderType === 'stems' && item.pricePerStem) {
          newPrice = getDiscountedPrice(item.pricePerStem, quantity);
        }
        return { ...item, quantity, price: newPrice };
      }
      return item;
    }));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getTotalAmount = useCallback(() => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cart]);

  const getTotalItems = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalAmount,
    getTotalItems,
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotalAmount, getTotalItems]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
